import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, Result, fmt,
  SpecCard, SpecValue,
} from "./_shared";

/**
 *  LSI — LANGELIER SATURATION INDEX
 *  --------------------------------------------------------------
 *  pHs = (9.3 + A + B) − (C + D)
 *    A = (log10(TDS) − 1) / 10
 *    B = −13.12 × log10(T_K) + 34.55           ; T_K = °C + 273
 *    C = log10(Ca²⁺ as CaCO₃) − 0.4
 *    D = log10(alkalinity as CaCO₃)
 *  LSI = pH − pHs
 *
 *  Valid for TDS roughly 50–10,000 mg/L.  For higher salinity use
 *  the Stiff & Davis index instead.
 *  --------------------------------------------------------------
 */
function lsiCompute({ pH, tempC, tds_mgL, ca_mgL, alk_mgL }) {
  if (!pH || !tempC || !tds_mgL || !ca_mgL || !alk_mgL) return null;
  const A = (Math.log10(tds_mgL) - 1) / 10;
  const B = -13.12 * Math.log10(tempC + 273.15) + 34.55;
  const C = Math.log10(ca_mgL) - 0.4;
  const D = Math.log10(alk_mgL);
  const pHs = (9.3 + A + B) - (C + D);
  return { pHs, LSI: pH - pHs };
}

function lsiInterpret(L) {
  if (L === null || L === undefined || Number.isNaN(L)) return { status: undefined, note: null, label: "—" };
  if (L < -2.0) return { status: "alert", label: "Highly corrosive",       note: "Severe corrosion expected — water dissolves CaCO₃ aggressively. Stabilise before distribution." };
  if (L < -0.5) return { status: "warn",  label: "Moderately corrosive",   note: "Tendency to dissolve CaCO₃ and metallic surfaces. Re-mineralise or pH-correct." };
  if (L <= 0.5) return { status: "ok",    label: "Balanced",                note: "Minimal scaling or corrosion — stable water." };
  if (L <= 2.0) return { status: "warn",  label: "Scale-forming",           note: "Tendency to deposit CaCO₃ scale. Watch heat exchangers and RO membranes." };
  return         { status: "alert", label: "Highly scale-forming",   note: "Severe scaling expected. Acid dose or softening required upstream of any heat / membrane process." };
}

export default function LSICalculator() {
  const [pH, setPH] = useState(7.6);
  const [tempC, setTempC] = useState(20);
  const [tds, setTds] = useState(400);          // mg/L
  const [ca, setCa] = useState(150);             // mg/L as CaCO3
  const [alk, setAlk] = useState(120);           // mg/L as CaCO3

  const calc = useMemo(() => lsiCompute({ pH, tempC, tds_mgL: tds, ca_mgL: ca, alk_mgL: alk }), [pH, tempC, tds, ca, alk]);
  const LSI = calc?.LSI ?? null;
  const pHs = calc?.pHs ?? null;
  const interp = lsiInterpret(LSI);

  return (
    <ToolShell
      slug="lsi"
      title="LSI — Langelier Saturation Index"
      oneLiner="Predicts whether water will dissolve or deposit calcium carbonate. The benchmark for cooling towers, pool chemistry and low-TDS drinking-water stability."
      onExport={() => ({
        inputs: [
          { label: "pH", value: pH },
          { label: "Water temperature", value: tempC, unit: "°C" },
          { label: "TDS", value: tds, unit: "mg/L" },
          { label: "Calcium hardness", value: ca, unit: "mg/L as CaCO₃" },
          { label: "Total alkalinity", value: alk, unit: "mg/L as CaCO₃" },
        ],
        results: [
          { label: "Saturation pH (pHs)", value: fmt(pHs), unit: "" },
          { label: "LSI",  value: LSI !== null ? (LSI >= 0 ? `+${fmt(LSI)}` : fmt(LSI)) : "—", unit: "",
            status: interp.status, note: `${interp.label}. ${interp.note ?? ""}` },
        ],
        notes: [
          "pHs = (9.3 + A + B) − (C + D)  ·  LSI = pH − pHs.",
          "A = (log₁₀(TDS) − 1)/10   ·  B = −13.12·log₁₀(T+273.15) + 34.55.",
          "C = log₁₀(Ca as CaCO₃) − 0.4   ·  D = log₁₀(alkalinity as CaCO₃).",
        ],
        spec: LSI !== null ? {
          title: "LSI verdict",
          body: `At pH <strong>${pH}</strong>, <strong>${tempC} °C</strong>, TDS <strong>${tds} mg/L</strong>, calcium <strong>${ca}</strong> and alkalinity <strong>${alk} mg/L as CaCO₃</strong>: pHs = <strong>${fmt(pHs, 2)}</strong>, LSI = <strong>${LSI >= 0 ? "+" : ""}${fmt(LSI, 2)}</strong>. Water is <strong>${interp.label.toLowerCase()}</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Field label="pH"><Plain value={pH} step="0.1" onChange={setPH} suffix="" testid="lsi-ph" /></Field>
        <Field label="Water temperature"><Plain value={tempC} onChange={setTempC} suffix="°C" testid="lsi-temp" /></Field>
        <Field label="TDS"><Plain value={tds} onChange={setTds} suffix="mg/L" testid="lsi-tds" /></Field>
        <Field label="Calcium hardness" hint="As CaCO₃ — most lab reports state this directly.">
          <Plain value={ca} onChange={setCa} suffix="mg/L" testid="lsi-ca" />
        </Field>
        <Field label="Total alkalinity" hint="As CaCO₃ — also called M-alkalinity.">
          <Plain value={alk} onChange={setAlk} suffix="mg/L" testid="lsi-alk" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <Result label="Saturation pH (pHs)"
          primary={{ value: fmt(pHs), unit: "" }}
          testid="lsi-result-phs" />
        <Result label={`LSI · ${interp.label}`}
          primary={{ value: LSI !== null ? (LSI >= 0 ? `+${fmt(LSI)}` : fmt(LSI)) : "—", unit: "" }}
          status={interp.status} note={interp.note}
          testid="lsi-result-lsi" />
      </div>

      {LSI !== null && (
        <SpecCard
          eyebrow="LSI verdict"
          testid="lsi-spec-card"
          footer={interp.note}
        >
          At pH <SpecValue>{pH}</SpecValue>,{" "}
          <SpecValue>{tempC} °C</SpecValue>, TDS{" "}
          <SpecValue>{tds} mg/L</SpecValue>, calcium{" "}
          <SpecValue>{ca}</SpecValue> and alkalinity{" "}
          <SpecValue>{alk} mg/L as CaCO₃</SpecValue>: pHs ={" "}
          <SpecValue>{fmt(pHs, 2)}</SpecValue>, LSI ={" "}
          <SpecValue>{LSI >= 0 ? "+" : ""}{fmt(LSI, 2)}</SpecValue>. Water is{" "}
          <SpecValue>{interp.label.toLowerCase()}</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}

function Plain({ value, onChange, suffix, step = "any", testid }) {
  // Decimal-safe — same string-buffer pattern as _shared.SuffixNum so
  // typing "0.5" or "8." mid-flow doesn't get stripped by React's
  // controlled-input reconciliation (parseFloat("8.")=8, equal to
  // existing 8, React re-renders "8" wiping the dot).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input type="text" inputMode="decimal" value={buf} placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
        data-testid={testid} />
      {suffix && (
        <span className="inline-flex items-center px-3 border border-l-0 font-mono"
          style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}>{suffix}</span>
      )}
    </div>
  );
}
